import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useCustomToast } from 'design-system';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useGetSupplierProfilesApi } from './use-get-supplier-profiles-api';

const endpoint = 'api/v1/cms/supplier-profiles';
export const updateSupplierProfileEndpointIdentifier = `${endpoint}/update`;

export interface UpdateSupplierProfileBody {
  supplier?: {
    name?: string;
    countryCode?: string;
    cutOffTime?: string | null;
    minimumOrder?: number | null;
    email?: string | null;
    isActive?: boolean;
    isPublic?: boolean;
    imageUrl?: string | null;
  };
  profile?: {
    businessName?: string;
    category?: string;
    moq?: string;
    delivery?: string;
    paymentTerms?: string;
    commissionRate?: number | null;
    billingLegalName?: string;
    billingAddress?: string;
    taxRegistrationNo?: string;
  };
}

type TData = Record<string, any>;
type TError = AxiosError;

type TVariables = {
  id: number;
  body: UpdateSupplierProfileBody;
};

type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useUpdateSupplierProfileApi(options?: Options): ReturnType {
  const { refetch: refetchSupplierProfiles } = useGetSupplierProfilesApi({ enabled: false });
  const toast = useCustomToast();

  const mutationFn = async ({ id, body }: TVariables) => {
    try {
      const { data } = await http.patch(`${endpoint}/${id}`, body);
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${endpoint}/update`],
    onSuccess: () => {
      refetchSupplierProfiles();

      toast({
        description: 'Supplier profile has been updated successfully',
        status: 'success',
      });
    },
    ...options,
  });
}
